import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, loadTokens, saveTokens, setAuthLostHandler } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => loadTokens()?.user ?? null);
  const [status, setStatus] = useState('checking'); // checking | anonymous | authenticated

  // A failed refresh anywhere in the app drops us back to the login screen.
  useEffect(() => {
    setAuthLostHandler(() => {
      setUser(null);
      setStatus('anonymous');
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!loadTokens()?.accessToken) {
        setStatus('anonymous');
        return;
      }
      try {
        // Validates the stored token and picks up any profile change made
        // elsewhere; the client also refreshes transparently on a 401.
        const data = await api.auth.me();
        if (cancelled) return;
        setUser(data.user);
        saveTokens({ ...loadTokens(), user: data.user });
        setStatus('authenticated');
      } catch {
        if (!cancelled) {
          saveTokens(null);
          setStatus('anonymous');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.auth.login(email, password);
    saveTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
    });
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const data = await api.auth.register(email, password, name);
    saveTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
    });
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    // Server-side logout bumps tokenVersion, invalidating every outstanding
    // refresh token — not just this browser's.
    await api.auth.logout().catch(() => {});
    saveTokens(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  /**
   * Replaces the cached user after a profile change. Writes through to storage
   * as well as state, so a reload does not briefly show the old preferences
   * before `/auth/me` comes back.
   */
  const updateUser = useCallback((next) => {
    setUser(next);
    const tokens = loadTokens();
    if (tokens) saveTokens({ ...tokens, user: next });
  }, []);

  const value = useMemo(
    () => ({ user, status, login, register, logout, setUser, updateUser }),
    [user, status, login, register, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

export default AuthProvider;
